import { Link } from '@tanstack/react-router';
import { AlertTriangle, Clock } from 'lucide-react';
import { Button } from '@/shared/components';
import type { PendingOnboardingResponse } from '@/shared/api';
import { formatDate } from '@/shared/lib/format';
import { REASON_PRESENTATION } from './pendingOnboardings';
import { OnboardingDeliveryDiagnostic } from './OnboardingDeliveryDiagnostic';

interface Props {
  row: PendingOnboardingResponse;
  busy: boolean;
  onReIssue: () => void;
  onRetry: () => void;
}

export function PendingOnboardingRow({ row, busy, onReIssue, onRetry }: Props) {
  const presentation = REASON_PRESENTATION[row.reason];

  return (
    <li className="flex flex-wrap items-start justify-between gap-3 py-3">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/bookings/$id"
            params={{ id: row.bookingId }}
            className="text-ink font-medium underline-offset-4 hover:underline"
          >
            {row.bookingReference}
          </Link>
          <span className="text-ink-soft text-sm">{row.guestName}</span>
          <span className="text-ink-soft text-xs">Anreise {formatDate(row.checkInDate)}</span>
        </div>
        <p className="text-ink-soft mt-1 flex items-start gap-1.5 text-sm">
          {presentation?.alarm ? (
            <AlertTriangle
              size={13}
              strokeWidth={2}
              className="text-sealwax mt-0.5 shrink-0"
              aria-hidden
            />
          ) : (
            <Clock size={13} strokeWidth={2} className="mt-0.5 shrink-0" aria-hidden />
          )}
          <span>
            <span className={presentation?.alarm ? 'text-sealwax font-medium' : 'font-medium'}>
              {presentation?.label ?? `Unbekannter Grund (${row.reason})`}
            </span>
            {presentation && ` — ${presentation.hint}`}
          </span>
        </p>
        {row.delivery && (
          <OnboardingDeliveryDiagnostic
            delivery={row.delivery}
            busy={busy}
            onRetry={onRetry}
            allowRetry={row.reason !== 'reissue_required'}
          />
        )}
      </div>
      {presentation?.alarm && (!row.delivery || row.reason === 'reissue_required') && (
        <Button variant="brand" className="min-h-11" loading={busy} onClick={onReIssue}>
          Zugang neu ausstellen
        </Button>
      )}
    </li>
  );
}
